import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/tokens.css';
import './styles/animations.css';
import './index.css';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';

// Store original console methods
const originalConsole = {
  log: console.log,
  debug: console.debug,
  info: console.info,
  warn: console.warn,
  error: console.error
};

// Override console methods based on environment
const overrideConsole = () => {
  const isProduction = process.env.NODE_ENV === 'production' || 
                      (import.meta && import.meta.env && import.meta.env.PROD);
  
  if (isProduction) {
    const noop = () => {};
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    // Uncomment to also suppress warnings and errors in production
    // console.warn = noop;
    // console.error = noop;
  }

  // Add restore function in development
  if (!isProduction) {
    // @ts-ignore - Adding to window for development convenience
    window.restoreConsole = () => {
      console.log = originalConsole.log;
      console.debug = originalConsole.debug;
      console.info = originalConsole.info;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
    };
  }
};

// Initialize console overrides
overrideConsole();

// !!!!Prevent ResizeObserver errors by overriding the ResizeObserver constructor
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const OriginalResizeObserver = window.ResizeObserver;
  
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        // Debounce the callback to prevent rapid firing
        requestAnimationFrame(() => {
          try {
            callback(entries, observer);
          } catch (error) {
            // Suppress ResizeObserver errors
            if (error instanceof Error && 
                (error.message.includes('ResizeObserver') || 
                 error.message.includes('loop completed'))) {
              return;
            }
            throw error;
          }
        });
      });
    }
  };
}

// Suppress ResizeObserver errors - More comprehensive approach
const originalError = console.error;
const originalWarn = console.warn;

console.error = (...args) => {
  const errorMessage = args[0]?.toString?.() || '';
  if (errorMessage.includes('ResizeObserver loop completed with undelivered notifications') ||
      errorMessage.includes('ResizeObserver loop limit exceeded')) {
    return;
  }
  originalError.apply(console, args);
};

console.warn = (...args) => {
  const warnMessage = args[0]?.toString?.() || '';
  if (warnMessage.includes('ResizeObserver') || warnMessage.includes('loop completed')) {
    return;
  }
  originalWarn.apply(console, args);
};

// Global error handler for ResizeObserver
window.addEventListener('error', (event) => {
  const errorMessage = event.message || '';
  if (errorMessage.includes('ResizeObserver') || 
      errorMessage.includes('loop completed') ||
      errorMessage.includes('undelivered notifications')) {
    event.preventDefault();
    event.stopPropagation();
    return false;
  }
});

// Additional error boundary for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason?.toString?.() || '';
  if (reason.includes('ResizeObserver') || reason.includes('loop completed')) {
    event.preventDefault();
    return false;
  }
});

// Suppress ResizeObserver errors in React's error boundary
const originalConsoleError = console.error;
console.error = (...args) => {
  const errorMessage = args[0]?.toString?.() || '';
  if (errorMessage.includes('ResizeObserver') || 
      errorMessage.includes('loop completed') ||
      errorMessage.includes('undelivered notifications')) {
    return;
  }
  originalConsoleError.apply(console, args);
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
